import React from 'react';
import { motion } from 'framer-motion';
import { useTheme, type ColorTheme } from '../hooks/useTheme';
import { MdCheck, MdOutlineColorLens } from 'react-icons/md';

/**
 * ThemePreview component
 * 
 * Displays a preview of the current theme with various UI elements
 * that show how the accent color is applied.
 */
const ThemePreview: React.FC = () => {
  const { colorTheme, setColorTheme } = useTheme();
  
  const colorOptions: Array<{name: string, value: ColorTheme, color: string}> = [
    { name: 'Blue', value: 'blue', color: '#00FFFF' },
    { name: 'Green', value: 'green', color: '#00FF00' },
    { name: 'Purple', value: 'purple', color: '#8B00FF' },
    { name: 'Pink', value: 'pink', color: '#FF00FF' },
    { name: 'Orange', value: 'orange', color: '#FF6B00' },
    { name: 'Yellow', value: 'yellow', color: '#FFFF00' },
  ];

  return (
    <div className="p-6 dark:bg-dark-surface bg-white rounded-lg shadow-lg border dark:border-gray-700 border-gray-200">
      <div className="flex items-center gap-3 mb-6">
        <MdOutlineColorLens className="w-6 h-6 text-theme" />
        <h2 className="text-xl font-bold dark:text-white text-gray-900">Theme Preview</h2>
      </div>
      
      {/* Color Picker */}
      <div className="mb-8">
        <h3 className="text-sm font-medium dark:text-gray-300 text-gray-700 mb-3">Accent Color</h3>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
          {colorOptions.map((color) => (
            <button
              key={color.value}
              onClick={() => setColorTheme(color.value)}
              style={{ backgroundColor: color.color }}
              className={`
                relative
                w-full aspect-square
                rounded-lg
                transition-all
                hover:scale-110
                ${colorTheme === color.value ? 'ring-4 ring-offset-2 dark:ring-offset-dark-surface ring-offset-white scale-110' : ''}
                shadow-lg
                hover:shadow-xl
              `}
              title={color.name}
            >
              {colorTheme === color.value && (
                <MdCheck className="w-6 h-6 text-white absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 drop-shadow-lg" />
              )}
            </button>
          ))}
        </div>
      </div>
      
      {/* UI Elements Preview */}
      <div className="space-y-6">
        {/* Text */}
        <div>
          <h3 className="text-sm font-medium dark:text-gray-300 text-gray-700 mb-3">Text Colors</h3>
          <div className="space-y-2">
            <p className="text-theme font-bold">Accent text color</p>
            <p>Normal text with <span className="text-theme">inline accent</span> color</p>
          </div>
        </div>
        
        {/* Buttons */}
        <div>
          <h3 className="text-sm font-medium dark:text-gray-300 text-gray-700 mb-3">Buttons</h3>
          <div className="flex flex-wrap gap-3">
            <button className="px-4 py-2 bg-theme text-white rounded-md">
              Primary Button
            </button>
            <button className="px-4 py-2 bg-transparent border-2 border-theme text-theme rounded-md">
              Outline Button
            </button>
            <button className="px-4 py-2 bg-theme-10 text-theme rounded-md">
              Light Button
            </button>
          </div>
        </div>
        
        {/* Cards */}
        <div>
          <h3 className="text-sm font-medium dark:text-gray-300 text-gray-700 mb-3">Cards & Borders</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="p-4 border-2 border-theme rounded-lg">
              Card with theme border
            </div>
            <div className="p-4 bg-theme-10 rounded-lg">
              Card with theme background
            </div>
            <div className="p-4 border-l-4 border-theme bg-dark-card dark:bg-dark-card rounded-lg">
              Card with accent border
            </div>
          </div>
        </div>
        
        {/* Effects */}
        <div>
          <h3 className="text-sm font-medium dark:text-gray-300 text-gray-700 mb-3">Effects</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <motion.div 
              whileHover={{ scale: 1.05 }} 
              className="p-4 flex items-center justify-center shadow-theme rounded-lg"
            >
              Shadow effect
            </motion.div>
            <motion.div 
              whileHover={{ scale: 1.05 }} 
              className="p-4 flex items-center justify-center glow-theme rounded-lg"
            >
              Glow effect
            </motion.div>
            <motion.div 
              whileHover={{ scale: 1.05 }} 
              className="p-4 flex items-center justify-center bg-gradient-to-r from-theme to-transparent text-white rounded-lg"
            >
              Gradient effect
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThemePreview;
